import React, { useState, useEffect, useContext, useRef } from 'react';
import { DndContext, useDraggable } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSwappingStrategy  } from '@dnd-kit/sortable';
import Widget from './Widget';
import { MasterCapabilitiesList, LMList } from '../utils/LMConstants';
import { formatShotData, generateGUID, getAllMeasurementsForLMInfo } from '../utils/Utilities';
import { Box } from "grommet";
import { SettingsIcon, Cross2Icon } from '../icons/Icons';
import { ThemeContext } from '../contexts/AppContext';
import { USER_PREFERENCES_IMPORTED_EVENT } from '../utils/userPreferences';

// Define a constant to control the number of highlighted widgets
const HIGHLIGHTED_WIDGET_COUNT = 7;
const LOCAL_STORAGE_KEY = 'savedMainWidgets';
const LOCAL_STORAGE_FAVORITES_KEY = 'favoriteWidgets';
const INITIAL_GRID_WIDTH = 9; // 10 columns wide
const INITIAL_GRID_HEIGHT = 5; // 6 rows tall
const INITIAL_GRID_SIZE = INITIAL_GRID_WIDTH * INITIAL_GRID_HEIGHT; // Total cells: 10 * 6 = 60
const EMPTY_PREFIX = 'empty-';

function Widgets({ currentSetup, currentShot }) {
    const [widgetIds, setWidgetIds] = useState([]);
    const [availableWidgets, setAvailableWidgets] = useState([]);
    const [widgetsData, setWidgetsData] = useState([]);
    const [showSettings, setShowSettings] = useState(false);
    const [selectedLaunchMonitor, setSelectedLaunchMonitor] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [draggingWidget, setDraggingWidget] = useState(null);
    const [draggingPosition, setDraggingPosition] = useState({ x: 0, y: 0 });
    const [draggingPositionStart, setDraggingPositionStart] = useState({ x: 0, y: 0 });
    const [allowedToDragNewWidget, setAllowedToDragNewWidget] = useState(false);
    const [pointerPosition, setPointerPosition] = useState({ x: 0, y: 0 });
    const [draggingToDelete, setDraggingToDelete] = useState(false);
    const [favoritedWidgets, setFavoritedWidgets] = useState([]);
    const [summaryValues, SetSummaryValues] = useState([]);
    const dragStartTimeoutRef = useRef(null);
    const pointerPositionRef = useRef({ x:0, y:0 });
    const HOLD_DELAY_MS = 200;
    const MOVE_CANCEL_THRESHOLD = 100;

    const { theme, setAppTheme } = useContext(ThemeContext);
    const [currentTheme, setCurrentTheme] = useState(theme);

    useEffect(() => {
        initializeAvailableWidgets();
        loadSavedWidgetIds();
        loadFavoritedWidget();

        document.addEventListener('mousemove', MouseMove);
        document.addEventListener('touchmove', TouchMove);

        return () => {
            document.removeEventListener('mousemove', MouseMove);
            document.removeEventListener('touchmove', TouchMove);
            clearDragStartTimeout();
        }
    }, []);

    useEffect(() => {
        const handleImportedPreferences = () => {
            loadSavedWidgetIds();
            loadFavoritedWidget();
        };

        window.addEventListener(USER_PREFERENCES_IMPORTED_EVENT, handleImportedPreferences);
        return () => {
            window.removeEventListener(USER_PREFERENCES_IMPORTED_EVENT, handleImportedPreferences);
        };
    }, []);

    useEffect(() => {
        setCurrentTheme(theme);
    }, [theme]);

    useEffect(() => {
        updateSummaryValues();
        updateWidgetsData();
    }, [currentSetup, currentShot, widgetIds]);

    useEffect(() => {
        updateSummaryValues();
    },[currentSetup]);
    
    useEffect(() => {
        updateWidgetsData();
    }, [summaryValues])

    useEffect(() => {
        if (widgetIds.length > 0) {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(widgetIds));
        }

        // loadSavedWidgetIds();
        // loadFavoritedWidget();
    }, [widgetIds]);

    function initializeAvailableWidgets() {
        const initialWidgets = LMList.flatMap(lm => {
            return lm.capabilities.map(capability => {
                const capabilityDetails = MasterCapabilitiesList.find(cap => cap.key === capability);

                if (!capabilityDetails) {
                    console.warn(`Capability ${capability} missing from MasterCapabilitiesList`);
                }

                const display = capabilityDetails?.display || capability;
                const unit = capabilityDetails?.unit || '';
                return {
                    id: `${lm.type}$${capability}`,
                    title: display,
                    value: '--',  // Default value when no current shot data is available
                    avg: '--',
                    std: '--',
                    unit: unit,
                    launchmonitor: lm.type,
                };
            });
        });
        setAvailableWidgets(initialWidgets);
    }

    function loadSavedWidgetIds() {
        const savedIds = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [];
        let filledIds = [...savedIds];

        filledIds = filledIds.map(id => 
            id.startsWith(EMPTY_PREFIX) ? `${EMPTY_PREFIX}${generateGUID()}` : id
        );

        const totalSlots = INITIAL_GRID_WIDTH * INITIAL_GRID_HEIGHT;

        while (filledIds.length < totalSlots) {
            filledIds.push(`${EMPTY_PREFIX}${generateGUID()}`);
        }

        filledIds = filledIds.slice(0, totalSlots);

        setWidgetIds(filledIds);
    }

    function loadFavoritedWidget() {
        const favWidgets = JSON.parse(localStorage.getItem(LOCAL_STORAGE_FAVORITES_KEY)) || [];

        setFavoritedWidgets(favWidgets);
    }

    function updateWidgetsData() {
        const updatedWidgets = widgetIds.map(widgetId => {
            const [lmType, capability] = widgetId.split('$');
            const lminfo = currentShot?.LMInfos.find(info => info.LMType === lmType);
            const measurements = getAllMeasurementsForLMInfo(lminfo);

            let value = '--';
            if (measurements[capability] !== undefined) {
                let dispValue = measurements[capability];
                const isNaNOrMissing = dispValue === undefined || dispValue === null || Number.isNaN(dispValue) || dispValue === "NaN";

                if (!isNaNOrMissing && dispValue) {
                    value = formatShotData(capability, dispValue);
                }
            }

            const widget = availableWidgets.find(w => w.id === widgetId);

            const sumValue = summaryValues.find(s => widget && s.key === widget.id);

            let avg = widget && widget.avg ? widget.avg : '--';
            let std = widget && widget.std ? widget.std : '--';
            if (sumValue) {
                const key = sumValue.key.split('$')[1];
                const avgFull = sumValue.value / sumValue.count;
                avg = formatShotData(key, avgFull, true);
                const stdFull = standardDeviation(sumValue.values);
                std = formatShotData(key, stdFull, true);
            }

            return {
                ...widget,
                value,
                avg,
                std
            };
        });

        setWidgetsData(updatedWidgets);
    }

    function updateSummaryValues() {
        SetSummaryValues([]);
        let summary = [];
        if (currentSetup && currentSetup.Shots && currentSetup.Shots.length > 0) {
            for (let i = 0; i < currentSetup.Shots.length; i++) {
                const shot = currentSetup.Shots[i];
                for (let j = 0; j < shot.LMInfos.length; j++) {
                    const lminfo = shot.LMInfos[j];
                    const measurements = getAllMeasurementsForLMInfo(lminfo);
                    for (let key in measurements) {
                        const newKey = lminfo.LMType + '$' + key;
                        const value =
                            measurements[key] === 'NaN'
                                ? measurements[key]
                                : formatShotData(key, measurements[key]);
                        if (shot.IsChecked) {
                            summary = AddItemToAvg(newKey, value, summary);
                        }
                    }
                }
            }
        }
        SetSummaryValues(summary);
    }

    function AddItemToAvg(key, value, existingAvg) {
        let avgData = existingAvg;// ? existingAvg : [];
        let existingItem = avgData.find(item => item.key === key);
        const isNaNOrMissing =
            value === undefined ||
            value === null ||
            Number.isNaN(value) ||
            value === 'NaN' ||
            Number(value) === -99999;
        if (!isNaNOrMissing) {
            if (existingItem) {
                existingItem.value += Number(value);
                existingItem.count += 1;
                existingItem.values.push(Number(value));
            } else {
                avgData.push({ key: key, value: Number(value), values: [Number(value)], count: 1 });
            }
            // SetColumnAvgs(avgData);
            return avgData;
        } else {
            return existingAvg;
        }
    }
    const updatePointerPosition = (x, y) => {
        setPointerPosition({ x, y });
        pointerPositionRef.current = { x, y };
    };
    function MouseMove(event){
        //setPointerPosition({x:event.clientX, y:event.clientY});
        updatePointerPosition(event.clientX, event.clientY);
    }
    function TouchMove(event){
        const touch = event.touches[0];
        //setPointerPosition({x:touch.clientX, y:touch.clientY});
        updatePointerPosition(touch.clientX, touch.clientY);
    }

    const clearDragStartTimeout = () => {
        if (dragStartTimeoutRef.current) {
            clearTimeout(dragStartTimeoutRef.current);
            dragStartTimeoutRef.current = null;
        }
    };

    const beginDelayedDrag = (widget, x, y) => {
        clearDragStartTimeout();
        dragStartTimeoutRef.current = setTimeout(() => {
            const ptest = setPointerPosition;
           
            //const deltaX = Math.abs((pointerPosition.x || x) - x);
            //const deltaY = Math.abs((pointerPosition.y || y) - y);
            const { x: currentX, y: currentY } = pointerPositionRef.current;
            const deltaX = Math.abs((currentX || x) - x);
            const deltaY = Math.abs((currentY || y) - y);

            if (deltaX > MOVE_CANCEL_THRESHOLD || deltaY > MOVE_CANCEL_THRESHOLD) {
                dragStartTimeoutRef.current = null;
                return;
            }

            setDraggingWidget(widget);
            setDraggingPosition({ x, y });
            setAllowedToDragNewWidget(true);///imworkinghere
            dragStartTimeoutRef.current = null;
        }, HOLD_DELAY_MS);
        
    };

    const handleMouseDown = (widget, event) => {
        setDraggingPositionStart({ x: event.clientX, y: event.clientY });
        //setPointerPosition({ x: event.clientX, y: event.clientY });
        updatePointerPosition(event.clientX, event.clientY );
        beginDelayedDrag(widget, event.clientX, event.clientY);
        event.preventDefault();
    };

    const handleTouchStart = (widget, event) => {
        const touch = event.touches[0];
        setDraggingPositionStart({ x: touch.clientX, y: touch.clientY });
        //setPointerPosition({ x: touch.clientX, y: touch.clientY });
        updatePointerPosition(touch.clientX, touch.clientY);
        beginDelayedDrag(widget, touch.clientX, touch.clientY);
         // event.preventDefault();
    };

    const handleTouchInSettings = (widget, event) =>{
        const touch = event.touches[0];
        const prevX = draggingPositionStart.x;
        const prevY = draggingPositionStart.y;

        if (!draggingWidget) {
            return;
        }

        const deltaX = touch.clientX - prevX;
        const deltaY = touch.clientY - prevY;

        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);

        if (absDeltaX > absDeltaY) {//ignore
            setAllowedToDragNewWidget(false);
        }else{
            setAllowedToDragNewWidget(true);
        }

        if(allowedToDragNewWidget){
            setDraggingPosition({ x: touch.clientX, y: touch.clientY });
            // event.preventDefault();
        }
    }

    const handleMouseMove = (event) => {
        if (draggingWidget) {
            setDraggingPosition({ x: event.clientX, y: event.clientY });
        } else if (dragStartTimeoutRef.current) {
            const deltaX = Math.abs(event.clientX - draggingPositionStart.x);
            const deltaY = Math.abs(event.clientY - draggingPositionStart.y);
            if (deltaX > 5 || deltaY > 5) { 
            
                clearDragStartTimeout();
            }
        }
    };

    const handleTouchMove = (event) => {
        const touch = event.touches[0];
        if (draggingWidget) {
            setDraggingPosition({ x: touch.clientX, y: touch.clientY });
        } else if (dragStartTimeoutRef.current) {
            const deltaX = Math.abs(touch.clientX - draggingPositionStart.x);
            const deltaY = Math.abs(touch.clientY - draggingPositionStart.y);
            //if (deltaX > 5 || deltaY > 5) {
            if (deltaY > deltaX && deltaY > 5) {
                clearDragStartTimeout();
            }
        }
    };

    const handleDragMove = (event) => {
        const mouseX = event.clientX || (event.touches && event.touches[0].clientX);
        const mouseY = event.clientY || (event.touches && event.touches[0].clientY);

        if (mouseX !== undefined && mouseY !== undefined) {
            setDraggingPosition({ x: mouseX, y: mouseY });
        }
    };

    const handleDragStart = (event) => {
        setDraggingToDelete(true);
    }

    const handleMouseUp = () => {
        clearDragStartTimeout();

        if (draggingWidget) {
            const mainArea = document.getElementById('main-area');
            const rect = mainArea.getBoundingClientRect();
    
            if (
                pointerPosition.x >= rect.left &&
                pointerPosition.x <= rect.right &&
                pointerPosition.y >= rect.top &&
                pointerPosition.y <= rect.bottom
            ) {
                setWidgetIds((prev) => {
                    const updatedIds = [...prev];
    
                    // Find all empty spots and their positions
                    const emptySpots = updatedIds
                        .map((id, index) => {
                            if (id.startsWith(EMPTY_PREFIX)) {
                                const column = index % INITIAL_GRID_WIDTH;
                                const row = Math.floor(index / INITIAL_GRID_WIDTH);
                                const x = rect.left + column * 195; // 195px is the width of each grid cell
                                const y = rect.top + row * 170; // 170px is the height of each grid cell
                                return { index, x, y };
                            }
                            return null;
                        })
                        .filter(Boolean);
    
                    if (emptySpots.length > 0) {
                        // Find the closest empty spot
                        const closestSpot = emptySpots.reduce((closest, spot) => {
                            const distance = Math.sqrt(
                                Math.pow(pointerPosition.x - spot.x - 195 / 2, 2) +
                                Math.pow(pointerPosition.y - spot.y - 170 / 2, 2)
                            );
                            if (distance < closest.distance) {
                                return { ...spot, distance };
                            }
                            return closest;
                        }, { distance: Infinity });
    
                        // Replace the closest empty spot with the new widget ID
                        updatedIds[closestSpot.index] = draggingWidget.id;
                    }
    
                    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedIds));
                    return updatedIds;
                });
            }
    
            setDraggingWidget(null);
        }

        setAllowedToDragNewWidget(false);
    };

    const handleTouchEnd = () => {
        handleMouseUp();
    };

    const handleDragEnd = (event) => {
        setDraggingToDelete(false);
        setAllowedToDragNewWidget(false);
        clearDragStartTimeout();
    
        const { active, over } = event;
    
        // Use the last known position of the mouse or touch from handleDragMove
        const mouseX = pointerPosition.x;
        const mouseY = pointerPosition.y;

        // Get the bounds of the menu area
        const menuArea = document.getElementById('menu-container');
        let overMenu = false;
        if (menuArea) {

            const rect = menuArea.getBoundingClientRect();
    
            // Check if the mouse is within the bounds of the menu area
            if (mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom) {
                overMenu = true;
                handleRemoveWidget(active.id);
            } else {
                overMenu = false;
            }
        }

        if(!overMenu)
        {
            if (!over || active.id === over.id) {
                return;
            }

            setWidgetIds((items) => {
                const oldIndex = items.indexOf(active.id);
                let newIndex;

                // Handle swap with empty spots
                if (over.id.startsWith('empty-')) {
                    newIndex = items.indexOf(over.id);
                } else {
                    newIndex = items.indexOf(over.id);
                }

                if (newIndex === -1 || oldIndex === -1) {
                    return items; // No valid swap, return the original array
                }

                let updatedItems = [...items];

                // Swap the widgets
                [updatedItems[oldIndex], updatedItems[newIndex]] = [updatedItems[newIndex], updatedItems[oldIndex]];

                // Update IDs starting with EMPTY_PREFIX to include a random GUID
                updatedItems = updatedItems.map(id =>
                    id.startsWith('empty-') ? `empty-${generateGUID()}` : id
                );

                return updatedItems;
            });
        }
        
    };

    const handleRemoveWidget = (id) => {
        if(favoritedWidgets.includes(id)){
            handleFavoriteWidget(id);
        }

        setWidgetIds((prevIds) => {
            const updatedIds = prevIds.map(widgetId => {
                if (widgetId === id) {
                    return `${EMPTY_PREFIX}${generateGUID()}`;  // Replace with an empty widget
                }
                return widgetId;
            });
            return updatedIds;
        });
    
        // Remove widget from availableWidgets, only if it's not already present
        const widget = availableWidgets.find(w => w.id === id);
        if (widget && !availableWidgets.some(w => w.id === id)) {
            setAvailableWidgets([...availableWidgets, widget]);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleFavoriteWidget = (id) => {
        let favWidgets = favoritedWidgets;
    
        if (!favWidgets.includes(id)) {
            favWidgets.push(id);
        } else {
            favWidgets = favWidgets.filter(i => i !== id);
        }
    
        // Ensure the favorite widgets are ordered according to widgetIds
        favWidgets.sort((a, b) => widgetIds.indexOf(a) - widgetIds.indexOf(b));
    
        setFavoritedWidgets(favWidgets);
        localStorage.setItem(LOCAL_STORAGE_FAVORITES_KEY, JSON.stringify(favWidgets));
    };

    const filteredWidgets = availableWidgets.map((widget) => {
        const [lmType, capability] = widget.id.split('$');
        const lminfo = currentShot?.LMInfos.find(info => info.LMType === lmType);

        let value = widget.value;
        
        if (lminfo && lminfo.Measurements[capability] !== undefined) {
            let dispValue = lminfo.Measurements[capability];
            const isNaNOrMissing = dispValue === undefined || dispValue === null || Number.isNaN(dispValue) || dispValue === "NaN";

            if (!isNaNOrMissing && dispValue) {
                value = formatShotData(capability, dispValue);
            }

            
        }

        const sumValue = summaryValues.find(s => s.key === widget.id);

        let avg = widget.avg;
        let std = widget.std;
        if (sumValue) {
            const key = sumValue.key.split('$')[1];
            const avgFull = sumValue.value / sumValue.count;
            avg = formatShotData(key, avgFull, true);
            const stdFull = standardDeviation(sumValue.values);
            std = formatShotData(key, stdFull, true);
        }

        return {
            ...widget,
            value,
            std,
            avg
        };
    }).filter(widget => {
        const matchesMonitor = selectedLaunchMonitor === 'all' || widget.launchmonitor === selectedLaunchMonitor;
        const capabilityDetails = MasterCapabilitiesList.find(cap => cap.key === widget.id.split('$')[1]);
        const matchesCategory = selectedCategory === 'all' || (capabilityDetails && capabilityDetails.category === selectedCategory);
        return matchesMonitor && matchesCategory;
    });

    function standardDeviation(numbers, isSample = true) {
    let n = numbers.length;
    if (n === 0) return 0; // Prevent division by zero
    
    // Step 1: Calculate mean
    let mean = numbers.reduce((acc, val) => acc + val, 0) / n;

    // Step 2: Calculate squared differences
    let squaredDiffs = numbers.map(num => Math.pow(num - mean, 2));

    // Step 3: Calculate variance
    let variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / (isSample ? n - 1 : n);

    // Step 4: Return standard deviation (sqrt of variance)
    return Math.sqrt(variance);
};
const n = currentSetup?.Shots?.length ?? 0;
    return (
        <div style={{width: '100%', height:'calc(100vh - 90px)'}}>
<div 
            style={{ position: 'relative', width: '100%', height: '100%', display:'flex', flexDirection:'column'}} 
            onMouseMove={handleMouseMove} 
            onMouseUp={handleMouseUp} 
            onTouchMove={handleTouchMove} 
            onTouchEnd={handleTouchEnd}
        >
            <button onClick={() => setShowSettings(!showSettings)} style={{ position: 'absolute', top: !showSettings ? -12 : 170, [showSettings ? 'right' : 'left'] : showSettings ? 10 : 0, zIndex: 100, background:'transparent', border:'0'}}>
                
                {!showSettings && (
                    <div style={{display:'flex', flexDirection:'row', justifyContent:'start'}}>
                        <SettingsIcon/>
                        <span style={{marginLeft:'5px', color:getComputedStyle(document.documentElement).getPropertyValue('--foreground-primary'), alignContent:'center', fontSize:'18px'}}>Widget Settings</span>
                         <span style={{marginLeft:'5px', color:getComputedStyle(document.documentElement).getPropertyValue('--foreground-primary'), alignContent:'center', fontSize:'18px'}}><div className='shotlist-panel-meta'>
                                     {n === 0 ? '0 out of ' :  (currentSetup?.Shots ?? []).filter(s => s.IsChecked).length+' out of '}{n === 0 ? 'no shots' : `${n} ${n === 1 ? 'shot' : 'shots'}`+' selected'}
                        </div> 
                        </span>                  
                         
                        
                            
                        



                    </div>
                    
                )}
                <></>
                {showSettings && (
                    <Cross2Icon width='40px' height='40px'/>
                )}
            </button>
            {showSettings && (
                <>
                {draggingToDelete && (
                    <div className='widget-delete-container'>
                        <div className='widget-delete-container-inner'>
                            Drag Here To Remove Widget
                        </div>
                    </div>
                    )}
                
                </>
            )}
            <div id='menu-container' disabled={!showSettings} style={{
                    // position: 'absolute',
                    // top: 0,
                    width: '100%',
                    height: !showSettings ? '0px' : '160px',
                    backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--background-tertiary'),
                    padding: showSettings ? '10px 10px 0 10px' : '0',
                    zIndex: 100,
                    display: 'flex',
                    flexShrink: '0',
                    transition: 'height 0.3s ease, height 0.3s ease',
                }}>
                    <div style={{ display: 'flex', gap: '20px', marginBottom: '10px' , flexDirection: 'column', alignItems:'start', visibility: showSettings ? 'visible' : 'hidden'}} >
                        <div style={{ display: 'flex' , flexDirection: 'column', alignItems:'center'}}>
                            <span className='widget-menu-span'>Launch Monitor:</span>
                            <select value={selectedLaunchMonitor} onChange={(e) => setSelectedLaunchMonitor(e.target.value)} className='widget-menu-combo'>
                                <option value="all">All</option>
                                {LMList.map(lm => (
                                    <option key={lm.type} value={lm.type}>{lm.type}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex' , flexDirection: 'column', alignItems:'center'}}>
                            <span className='widget-menu-span'>Category:</span>
                            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className='widget-menu-combo'>
                                <option value="all">All</option>
                                {[...new Set(MasterCapabilitiesList.map(cap => cap.category))].map(category => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div id="menu-area" style={{ display: 'flex', gap: '10px', overflowX: allowedToDragNewWidget ? 'hidden' : 'auto', overflowY: 'hidden' }}>
                        {filteredWidgets.map((widget) => {
                            const isInMainArea = widgetIds.includes(widget.id);
                            return (
                                <div
                                    key={widget.id}
                                    onMouseDown={isInMainArea ? null : (e) => handleMouseDown(widget, e)}
                                    onTouchStart={isInMainArea ? null : (e) => handleTouchStart(widget, e)}
                                    onTouchMove={isInMainArea ? null : (e) => handleTouchInSettings(widget, e)}
                                    style={{
                                            cursor: isInMainArea ? 'not-allowed' : 'grab',
                                            opacity: isInMainArea ? 0.5 : 1,
                                            // Allow horizontal scrolling normally; lock it only when we're actually dragging
                                            touchAction: allowedToDragNewWidget ? 'none' : 'pan-x',
                                        }}
                                >
                                    <Widget
                                        id={`menu-${widget.id}`}
                                        title={widget.title}
                                        value={widget.value}
                                        unit={widget.unit}
                                        avg={widget.avg}
                                        std={widget.std}
                                        launchmonitor={widget.launchmonitor}
                                        isFavorite={false}
                                        isEditing={false}
                                        touchAction="pan-x"
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>

            <div style={{
                    flexShrink: '1',
                    padding: '10px',
                    marginBottom: '10px',
                    zIndex: 1,
                    overflow: !draggingToDelete ? 'auto' : 'hidden',
            }}>
<div
                style={{
                    height:'100%',
                    width:'100%'
                }}
                id="main-area"
                onDragOver={handleDragOver}
            >

            <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} autoScroll={false}>
                <SortableContext items={widgetIds} strategy={rectSwappingStrategy} disabled={!showSettings}>
                    <Box
                        direction="row"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat('+ INITIAL_GRID_WIDTH +', 195px)',
                            gridTemplateRows: 'repeat('+ INITIAL_GRID_HEIGHT +', 170px)',
                            gap: '10px',
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        {widgetIds.map((widgetId, index) => {
                            const widgetData = widgetId.startsWith(EMPTY_PREFIX)
                                ? null
                                : widgetsData.find(w => w.id === widgetId);

                            return (
                                <div
                                    key={widgetId}
                                    style={{
                                        gridColumn: `${(index % INITIAL_GRID_WIDTH) + 1}`,
                                        gridRow: `${Math.floor(index / INITIAL_GRID_WIDTH) + 1}`,
                                        // minHeight: '175px',
                                        // maxWidth: '195px',
                                        border: (draggingToDelete || draggingWidget) ? '2px dashed #ccc' : '0px' ,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: widgetData ? 'transparent' : 'transparent',
                                        touchAction: 'none',
                                    }}
                                >
                                    <Widget
                                        id={widgetId}
                                        title={widgetData ? widgetData.title : ''}
                                        value={widgetData ? widgetData.value : ''}
                                        unit={widgetData ? widgetData.unit : ''}
                                        avg={widgetData ? widgetData.avg : ''}
                                        std={widgetData ? widgetData.std : ''}
                                        launchmonitor={widgetData ? widgetData.launchmonitor : ''}
                                        isFavorite={favoritedWidgets.includes(widgetId)}
                                        SetFavorite={handleFavoriteWidget}
                                        isEditing={showSettings}
                                    />
                                </div>
                            );
                        })}
                    </Box>
                    </SortableContext>
            </DndContext>
                

                {draggingWidget && (
                    <div
                        style={{
                            position: 'fixed',
                            top: draggingPosition.y,
                            left: draggingPosition.x,
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1000,
                            pointerEvents: 'none',
                        }}
                    >
                        <Widget
                            id={`dragging-${draggingWidget.id}`}
                            title={draggingWidget.title}
                            value={draggingWidget.value}
                            unit={draggingWidget.unit}
                            std={draggingWidget.std}
                            avg={draggingWidget.avg}
                            launchmonitor={draggingWidget.launchmonitor}
                            isFavorite={favoritedWidgets.includes(draggingWidget.id)}
                        />
                    </div>
                )}
            </div>
            </div>
            
        </div>
        </div>
    );
}

export default Widgets;
